import React, { useState, useEffect } from "react";
import { useEvents } from "../../../hooks/useEvents";
import { useCalendar } from "../../../hooks/useCalendar";
import CalendarPopup from "../../../components/CalendarPopup/CalendarPopup";

import "./HomeAgenda.css";

// Podem mantenir les imatges com a fallback per si un esdeveniment no té imatge
import fallbackImage from "../../../assets/images/socis/imatge1.jpg";

function HomeAgenda() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [startX, setStartX] = useState(null);

  const {
    openCalendarMenu,
    handleCalendarClick,
    closeCalendarMenu,
    addToAppleOutlookCalendar,
    getGoogleCalendarURL,
  } = useCalendar();

  const { esdeveniments, loadingEsdeveniments } = useEvents();

  // Filtrar solo los eventos presentes o futuros
  const currentDate = new Date();
  // Establece la hora a 00:00:00 para comparar solo la fecha
  currentDate.setHours(0, 0, 0, 0);

  // Filtrar los eventos que ocurren hoy o en el futuro
  const futureEvents =
    esdeveniments?.filter((event) => {
      const eventDate = new Date(event.data);
      // Establece la hora a 00:00:00 para comparar solo la fecha
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= currentDate;
    }) || [];

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!startX) return;

    const diff = e.changedTouches[0].clientX - startX;
    if (Math.abs(diff) > 50) {
      // umbral de 50px para cambiar de slide
      if (diff > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (diff < 0 && currentIndex < (futureEvents?.length || 0) - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
    setStartX(null);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => prevIndex - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => prevIndex + 1);
  };

  // Si no hi ha esdeveniments o encara s'estan carregant, mostrem un indicador de càrrega
  if (loadingEsdeveniments || !esdeveniments) {
    return (
      <section className="home-agenda">
        <div className="home-agenda__container">
          <div className="home-agenda__sidebar">
            <h2>AGENDA</h2>
          </div>
          <div className="home-agenda__carousel">
            <p>Carregant esdeveniments...</p>
          </div>
        </div>
      </section>
    );
  }

  // Si no hi ha esdeveniments futurs després de carregar
  if (futureEvents.length === 0) {
    return (
      <section className="home-agenda">
        <div className="home-agenda__container">
          <div className="home-agenda__sidebar">
            <h2>AGENDA</h2>
          </div>
          <div className="home-agenda__carousel">
            <p>No hi ha esdeveniments futurs programats.</p>
          </div>
        </div>
      </section>
    );
  }

  const translateValue = isMobile ? currentIndex * 100 : currentIndex * 35;
  const isFirstSlide = currentIndex === 0;
  const isLastSlide = currentIndex === futureEvents.length - 2;

  // Funció per formatar la data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();

    // Array de noms de mesos en català (format abreviat)
    const monthNames = [
      "gen",
      "feb",
      "març",
      "abr",
      "maig",
      "juny",
      "jul",
      "ago",
      "set",
      "oct",
      "nov",
      "des",
    ];

    const month = monthNames[date.getMonth()];

    return { day, month };
  };

  return (
    <section className="home-agenda">
      <div className="home-agenda__container">
        <div className="home-agenda__sidebar">
          <h2>AGENDA</h2>
        </div>

        <div className="home-agenda__carousel">
          <div
            className="home-agenda__carousel-track"
            style={{
              transform: `translateX(-${translateValue}%)`,
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {futureEvents.map((event, index) => {
              const { day, month } = formatDate(event.data);
              return (
                <div key={index} className="home-agenda__carousel-card">
                  <div className="home-agenda__carousel-card-date">
                    <span className="date">{day}</span>
                    <span className="month">{month}</span>
                  </div>
                  <div className="home-agenda__carousel-card-image">
                    <img
                      src={event.imatge?.url || fallbackImage}
                      alt={event.titol}
                    />
                  </div>
                  <div className="home-agenda__carousel-card-content">
                    <div>
                      <h3 className="home-agenda__carousel-card-title">
                        <a href="/agenda" className="link-without-underline">
                          {event.titol}
                        </a>
                      </h3>
                      <p className="home-agenda__carousel-card-description">
                        {event.descripcio.length > 150
                          ? `${event.descripcio.substring(0, 150)}...`
                          : event.descripcio}
                      </p>
                    </div>
                    <div style={{ position: "relative" }}>
                      <a
                        href="#calendari"
                        className="afegir-al-calendari"
                        onClick={(e) => handleCalendarClick(event, index, e)}
                      >
                        AFEGIR AL MEU CALENDARI
                      </a>

                      {/* Componente de menú de calendario */}
                      {openCalendarMenu === index && (
                        <CalendarPopup
                          event={event}
                          onAddToAppleOutlook={addToAppleOutlookCalendar}
                          getGoogleCalendarURL={getGoogleCalendarURL}
                          onClose={closeCalendarMenu}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="home-agenda__footer">
          <div className="home-agenda__controls">
            <button
              onClick={handlePrev}
              className="home-agenda__controls-button prev"
              disabled={isFirstSlide}
            >
              &larr;
            </button>
            <button
              onClick={handleNext}
              className="home-agenda__controls-button next"
              disabled={isLastSlide}
            >
              &rarr;
            </button>
          </div>
          <a href="/agenda">VEURE AGENDA</a>
        </div>
      </div>
    </section>
  );
}

export default HomeAgenda;
